import { useCallback, useEffect, useRef, useState } from 'react';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantsService from '@services/ParticipantsService';

const ASSISTANT_SOUND_DEFAULT = 1;
const DEBOUNCE_MS = 1000;

export const useAssistantSound = () => {
  const { user } = useMytaverse();

  const requestTimeout = useRef<NodeJS.Timeout | null>(null);
  // Tracks the last value read from the profile or saved to the API so the
  // save effect can skip the initial load and avoid a redundant write.
  const lastSaved = useRef<number | null>(null);
  const initialized = useRef(false);

  const [assistantSound, setAssistantSoundState] = useState(ASSISTANT_SOUND_DEFAULT);

  useEffect(() => {
    if (initialized.current || !user) {
      return;
    }
    initialized.current = true;
    const value = user.assistantSound ?? ASSISTANT_SOUND_DEFAULT;
    lastSaved.current = value;
    setAssistantSoundState(value);
  }, [user]);

  useEffect(() => {
    if (lastSaved.current === null || lastSaved.current === assistantSound) {
      return;
    }

    if (requestTimeout.current) {
      clearTimeout(requestTimeout.current);
      requestTimeout.current = null;
    }

    requestTimeout.current = setTimeout(async () => {
      lastSaved.current = assistantSound;

      await ParticipantsService.updateCurrentParticipantProfile({ assistantSound });

      requestTimeout.current = null;
    }, DEBOUNCE_MS);

    return () => {
      if (requestTimeout.current) {
        clearTimeout(requestTimeout.current);
        requestTimeout.current = null;
      }
    };
  }, [assistantSound]);

  const setAssistantSoundHandle = useCallback((level: number) => {
    setAssistantSoundState(level);
  }, []);

  return {
    assistantSound,
    setAssistantSoundHandle,
  };
};
